// src/components/ui/button.jsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // base
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-akai-gold/40 " +
    "disabled:pointer-events-none disabled:opacity-50 select-none " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // ✅ your “brand default”
        default:
          "rounded-full bg-akai-gold2 text-white shadow-sm hover:bg-akai-gold2/90 active:scale-[0.99]",

        // good for dark / important actions
        dark:
          "rounded-full bg-stone-900 text-white shadow-sm hover:bg-stone-800 active:scale-[0.99]",

        // destructive
        destructive:
          "rounded-full bg-red-600 text-white shadow-sm hover:bg-red-700 active:scale-[0.99]",

        // outline
        outline:
          "rounded-full border border-stone-300 bg-white text-stone-900 hover:bg-stone-100",

        // soft / secondary
        secondary:
          "rounded-full bg-stone-100 text-stone-900 hover:bg-stone-200",

        // ghost (no border, subtle hover)
        ghost:
          "rounded-full bg-transparent text-stone-800 hover:bg-stone-100",

        // link style
        link: "bg-transparent p-0 h-auto text-akai-gold2 underline underline-offset-4 hover:text-akai-gold",
      },

      size: {
        default: "h-10 px-5 text-sm",
        sm: "h-9 px-4 text-sm",
        lg: "h-11 px-7 text-base",
        icon: "h-10 w-10 rounded-full p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        type={asChild ? undefined : type ?? "button"}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };